"use client";

import { useActivePartnerCode } from "@/lib/useActivePartnerCode";
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useSitePathname } from "@/lib/i18n/useSitePathname";
import { ArrowRight, Mail, Phone, Video, X } from "lucide-react";
import Link from "@/components/LocaleLink";
import { WhatsAppIcon, TelegramIcon } from "@/components/BrandIcons";
import { useTranslation } from "@/lib/i18n/LanguageContext";
import { SITE, isSupportOpen, mailLink, telLink, telegramLink, whatsappLink } from "@/lib/config";
import { isDockSuppressed } from "@/components/landing/ConversionDock";
import { cn } from "@/lib/utils";

/**
 * The floating contact bubble, bottom right (operator, 28.09.2026: he liked
 * the live site's "Wie dürfen wir uns melden?" bubble and wants Telegram in
 * it). One tap opens a small panel with every channel: WhatsApp, Telegram,
 * phone, e-mail and the video consultation. The WhatsApp button breathes only
 * while someone is actually there (SUPPORT_HOURS), like the header badge.
 * Left out on the form flows (same list as the dock), where it could cover a
 * submit button, and hidden while the phone menu is open (.floating-cta).
 */
const COPY = {
  de: {
    title: "Wie dürfen wir uns melden?",
    online: "Wir sind jetzt online",
    hours: "Mo–So 8–22 Uhr",
    whatsappSub: "Meist in wenigen Stunden",
    callSub: SITE.phone,
    emailSub: SITE.email,
    video: "Video-Beratung",
    videoSub: "Kostenlos, 15 Minuten",
    open: "Kontakt aufnehmen: Wie dürfen wir uns melden?",
    close: "Schliessen",
    call: "Anrufen",
    email: "E-Mail",
  },
  en: {
    title: "How would you like to reach us?",
    online: "We're online now",
    hours: "Mon–Sun 8 am–10 pm",
    whatsappSub: "Usually within a few hours",
    callSub: SITE.phone,
    emailSub: SITE.email,
    video: "Video consultation",
    videoSub: "Free, 15 minutes",
    open: "Contact us: how would you like to reach us?",
    close: "Close",
    call: "Call",
    email: "Email",
  },
  da: {
    title: "Hvordan vil I kontakte os?",
    online: "Vi er online nu",
    hours: "Man–søn 8–22",
    whatsappSub: "Oftest inden for få timer",
    callSub: SITE.phone,
    emailSub: SITE.email,
    video: "Videorådgivning",
    videoSub: "Gratis, 15 minutter",
    open: "Kontakt os: hvordan vil I kontakte os?",
    close: "Luk",
    call: "Ring",
    email: "E-mail",
  },
  ar: {
    title: "كيف تفضّلون التواصل معنا؟",
    online: "نحن متصلون الآن",
    hours: "الاثنين–الأحد، 8 صباحًا–10 مساءً",
    whatsappSub: "عادةً خلال ساعات قليلة",
    callSub: SITE.phone,
    emailSub: SITE.email,
    video: "استشارة بالفيديو",
    videoSub: "مجانية، 15 دقيقة",
    open: "تواصلوا معنا: كيف تفضّلون الوصول إلينا؟",
    close: "إغلاق",
    call: "اتصال",
    email: "البريد الإلكتروني",
  },
  uk: {
    title: "Як вам зручніше з нами зв’язатися?",
    online: "Ми зараз онлайн",
    hours: "Пн–Нд 8:00–22:00",
    whatsappSub: "Зазвичай протягом кількох годин",
    callSub: SITE.phone,
    emailSub: SITE.email,
    video: "Відеоконсультація",
    videoSub: "Безкоштовно, 15 хвилин",
    open: "Зв’язатися з нами: як вам зручніше?",
    close: "Закрити",
    call: "Зателефонувати",
    email: "Електронна пошта",
  },
  ru: {
    title: "Как вам удобнее связаться с нами?",
    online: "Мы сейчас онлайн",
    hours: "Пн–Вс, 8:00–22:00",
    whatsappSub: "Обычно в течение нескольких часов",
    callSub: SITE.phone,
    emailSub: SITE.email,
    video: "Видеоконсультация",
    videoSub: "Бесплатно, 15 минут",
    open: "Связаться с нами: как вам удобнее?",
    close: "Закрыть",
    call: "Позвонить",
    email: "Эл. почта",
  },
  vi: {
    title: "Các bạn muốn liên hệ với chúng tôi qua kênh nào?",
    online: "Chúng tôi đang trực tuyến",
    hours: "Thứ Hai–Chủ nhật, 8:00–22:00",
    whatsappSub: "Thường trong vòng vài giờ",
    callSub: SITE.phone,
    emailSub: SITE.email,
    video: "Tư vấn qua video",
    videoSub: "Miễn phí, 15 phút",
    open: "Liên hệ: các bạn muốn liên hệ với chúng tôi qua kênh nào?",
    close: "Đóng",
    call: "Gọi điện",
    email: "Email",
  },
  es: {
    title: "¿Cómo prefieren contactarnos?",
    online: "Estamos en línea ahora",
    hours: "Lun–dom 8–22 h",
    whatsappSub: "Normalmente en pocas horas",
    callSub: SITE.phone,
    emailSub: SITE.email,
    video: "Asesoría por videollamada",
    videoSub: "Gratis, 15 minutos",
    open: "Contacto: ¿cómo prefieren contactarnos?",
    close: "Cerrar",
    call: "Llamar",
    email: "Correo",
  },
} as const;

function subscribeScroll(onChange: () => void) {
  window.addEventListener("scroll", onChange, { passive: true });
  return () => window.removeEventListener("scroll", onChange);
}
// On a phone the bubble waits for the first scroll: at the very top it would
// sit on the hero's red button on small screens, and the header already shows
// WhatsApp and Telegram there.
const hasScrolled = () => window.scrollY > 80;

export function ContactBubble() {
  const { locale } = useTranslation();
  const lang = locale;
  const c = COPY[lang];
  const pathname = useSitePathname();
  const partnerCode = useActivePartnerCode();
  // Open on the page it was opened on; a page change closes it by itself.
  const [openOn, setOpenOn] = useState<string | null>(null);
  const open = openOn !== null && openOn === pathname;
  // null until the clock has been read after mount, so the server paint never
  // claims "online" at night.
  const [online, setOnline] = useState<boolean | null>(null);
  const scrolled = useSyncExternalStore(subscribeScroll, hasScrolled, () => false);
  // The question pill shows once, for six seconds after the first scroll, then
  // fades and gives the page back; the round button stays (operator,
  // 18.10.2026: while scrolling it covered card text, FAQ toggles and the
  // footer's phone number). Once per visit: the bubble lives in the layout.
  const [label, setLabel] = useState<"show" | "fade" | "done">("show");
  useEffect(() => {
    if (!scrolled || label === "done") return;
    const id = window.setTimeout(
      () => setLabel(label === "show" ? "fade" : "done"),
      label === "show" ? 6000 : 400,
    );
    return () => window.clearTimeout(id);
  }, [scrolled, label]);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const update = () => setOnline(isSupportOpen());
    update();
    const id = window.setInterval(update, 60_000);
    return () => window.clearInterval(id);
  }, []);

  const close = useCallback((refocus = true) => {
    setOpenOn(null);
    if (refocus) launcherRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    panelRef.current?.querySelector<HTMLElement>("a")?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, close]);

  if (isDockSuppressed(pathname)) return null;

  const rows = [
    {
      key: "whatsapp",
      href: whatsappLink(undefined, lang, partnerCode),
      external: true,
      title: "WhatsApp",
      sub: c.whatsappSub,
      icon: (
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#25D366] text-white">
          <WhatsAppIcon mono className="h-5 w-5" />
        </span>
      ),
    },
    {
      key: "telegram",
      href: telegramLink(),
      external: true,
      title: "Telegram",
      sub: SITE.telegramHandle,
      icon: (
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#229ED9] text-white">
          <TelegramIcon mono className="h-5 w-5" />
        </span>
      ),
    },
    {
      key: "call",
      href: telLink(),
      external: false,
      title: c.call,
      sub: c.callSub,
      icon: (
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-cream-100 text-navy-800 ring-1 ring-navy-100">
          <Phone className="h-[18px] w-[18px]" strokeWidth={1.8} aria-hidden />
        </span>
      ),
    },
    {
      key: "email",
      href: mailLink(),
      external: false,
      title: c.email,
      sub: c.emailSub,
      icon: (
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-cream-100 text-navy-800 ring-1 ring-navy-100">
          <Mail className="h-[18px] w-[18px]" strokeWidth={1.8} aria-hidden />
        </span>
      ),
    },
  ];

  const rowClass =
    "group flex items-center gap-3 rounded-xl px-2.5 py-2.5 transition-colors hover:bg-cream-100 focus-visible:bg-cream-100 focus-visible:outline-none";

  return (
    <div className="floating-cta">
      {open && (
        <button
          type="button"
          aria-label={c.close}
          tabIndex={-1}
          onClick={() => close(false)}
          className="fixed inset-0 z-[44] cursor-default bg-navy-900/20 backdrop-blur-[2px]"
        />
      )}

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="false"
          aria-label={c.title}
          className="fixed end-3 z-[46] w-[min(21rem,calc(100vw-1.5rem))] origin-bottom-right overflow-hidden rounded-2xl border border-navy-100 bg-white shadow-[0_24px_60px_-20px_rgba(15,31,60,0.45)] sm:end-6 lg:end-8"
          style={{
            bottom: "calc(max(env(safe-area-inset-bottom), 0px) + 5.25rem)",
            animation: "var(--animate-fade-up)",
            animationDuration: "180ms",
          }}
        >
          <div className="flex items-start justify-between gap-3 border-b border-navy-100 bg-cream-50 px-4 py-3.5">
            <div>
              <p className="font-display text-[16px] font-semibold leading-snug text-ink-900">{c.title}</p>
              <p className="mt-1 flex items-center gap-1.5 text-[12px] text-navy-500">
                {online && <span className="inline-block h-2 w-2 rounded-full bg-[#25D366]" aria-hidden />}
                {online ? c.online : c.hours}
              </p>
            </div>
            <button
              type="button"
              aria-label={c.close}
              onClick={() => close()}
              className="-me-1 grid h-9 w-9 place-items-center rounded-full text-navy-500 transition-colors hover:bg-white hover:text-coral-700"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
          <div className="space-y-0.5 p-2">
            {rows.map((r) => (
              <a
                key={r.key}
                href={r.href}
                {...(r.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                data-track={`bubble_${r.key}`}
                onClick={() => close(false)}
                className={rowClass}
              >
                {r.icon}
                <span className="min-w-0 flex-1">
                  <span className="block text-[14.5px] font-semibold text-ink-900">{r.title}</span>
                  <span className="block truncate text-[12.5px] text-navy-500"><bdi>{r.sub}</bdi></span>
                </span>
                <ArrowRight className="h-4 w-4 shrink-0 text-navy-300 transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5 group-hover:text-coral-600" aria-hidden />
              </a>
            ))}
            <Link href="/beratung" data-track="bubble_video" onClick={() => close(false)} className={rowClass}>
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-coral-50 text-coral-700 ring-1 ring-coral-100">
                <Video className="h-[18px] w-[18px]" strokeWidth={1.8} aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[14.5px] font-semibold text-ink-900">{c.video}</span>
                <span className="block truncate text-[12.5px] text-navy-500">{c.videoSub}</span>
              </span>
              <ArrowRight className="h-4 w-4 shrink-0 text-navy-300 transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5 group-hover:text-coral-600" aria-hidden />
            </Link>
          </div>
        </div>
      )}

      {/* The launcher: from sm the question as a white pill beside the round
          button; on a phone only the round button, so it covers as little of
          the page as possible. */}
      <div
        className={cn(
          "fixed end-3 z-[45] flex items-center gap-2.5 transition-all duration-300 sm:end-6 lg:end-8",
          !scrolled && !open && "max-sm:pointer-events-none max-sm:translate-y-6 max-sm:opacity-0",
        )}
        style={{ bottom: "calc(max(env(safe-area-inset-bottom), 0px) + 0.75rem)" }}
      >
        {/* The question pill waits for the first scroll at every width: on a
            tablet it covered the hero link right away (visual review 17.10.2026). */}
        {!open && scrolled && label !== "done" && (
          <button
            type="button"
            onClick={() => setOpenOn(pathname)}
            data-track="bubble_open_label"
            className={cn(
              "hidden h-11 items-center rounded-full border border-navy-100 bg-white px-4 text-[14px] font-semibold text-ink-900 shadow-[0_12px_30px_-14px_rgba(15,31,60,0.45)] transition duration-300 hover:-translate-y-px hover:text-coral-700 sm:inline-flex",
              label === "fade" && "pointer-events-none opacity-0",
            )}
          >
            {c.title}
          </button>
        )}
        <button
          ref={launcherRef}
          type="button"
          aria-label={open ? c.close : c.open}
          aria-expanded={open}
          data-track="bubble_open"
          onClick={() => (open ? close() : setOpenOn(pathname))}
          className={cn(
            "relative grid h-13 w-13 place-items-center rounded-full bg-[#25D366] text-white sm:h-14 sm:w-14 shadow-[0_14px_32px_-12px_rgba(18,140,126,0.75)] transition-transform hover:scale-105",
            online && !open && "live-ring",
          )}
        >
          {open ? <X className="h-6 w-6" aria-hidden /> : <WhatsAppIcon mono className="h-6 w-6 sm:h-7 sm:w-7" />}
          {online && !open && (
            <span aria-hidden className="absolute -end-0.5 -top-0.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-[#25D366]" />
          )}
        </button>
      </div>
    </div>
  );
}
